import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { Field } from "./Field";

/**
 * Settings → Browser extension. Pairs the Thrifty Crosslister extension in
 * this browser (the extension's content script picks the token up), or shows
 * a code to paste into it, and lists every paired browser.
 */

interface Device {
  id: string;
  name: string;
  version: string | null;
  lastSeenAt: string | null;
  online: boolean;
  status: { signedIn?: Record<string, boolean>; paused?: boolean };
}

interface Recording {
  id: string;
  sites: string[];
  eventCount: number;
  bytes: number;
  createdAt: string;
  deviceName: string | null;
}

const ago = (iso: string | null) => {
  if (!iso) return "never";
  const m = Math.round((Date.now() - Date.parse(iso)) / 60000);
  return m < 1 ? "just now" : m < 60 ? `${m} min ago` : m < 2880 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} days ago`;
};

export function ExtensionDevices() {
  const [devices, setDevices] = useState<Device[]>([]);
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [present, setPresent] = useState<{ paired: boolean; name: string | null } | null>(null);
  const [name, setName] = useState("");
  const [code, setCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [d, r] = await Promise.all([
        api.get<{ devices: Device[] }>("/extension/devices"),
        api.get<{ recordings: Recording[] }>("/extension/recordings"),
      ]);
      setDevices(d.devices);
      setRecordings(r.recordings);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Could not load paired browsers");
    }
  }, []);

  useEffect(() => {
    void load();
    const onMsg = (e: MessageEvent) => {
      if (e.source !== window || e.origin !== window.location.origin) return;
      const d = e.data as { type?: string; paired?: boolean; name?: string | null; ok?: boolean };
      if (d?.type === "thrifty-ext-present") setPresent({ paired: Boolean(d.paired), name: d.name ?? null });
      if (d?.type === "thrifty-ext-paired") {
        setMessage(d.ok ? "Paired. This browser will start posting and watching sales within a minute." : "The extension didn't accept the pairing.");
        window.postMessage({ type: "thrifty-ext-ping" }, window.location.origin);
        void load();
      }
    };
    window.addEventListener("message", onMsg);
    window.postMessage({ type: "thrifty-ext-ping" }, window.location.origin);
    return () => window.removeEventListener("message", onMsg);
  }, [load]);

  async function pair(handToExtension: boolean) {
    setBusy(true);
    setMessage(null);
    try {
      const deviceName = name.trim() || "This browser";
      const res = await api.post<{ token: string }>("/extension/devices", { name: deviceName });
      if (handToExtension) {
        window.postMessage({ type: "thrifty-ext-token", token: res.token, name: deviceName }, window.location.origin);
      } else {
        setCode(res.token);
      }
      await load();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Could not pair");
    } finally {
      setBusy(false);
    }
  }

  async function download(r: Recording) {
    const full = await api.get<unknown>(`/extension/recordings/${r.id}`);
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([JSON.stringify(full, null, 1)], { type: "application/json" }));
    a.download = `thrifty-recording-${r.sites.join("-") || "session"}-${r.createdAt.slice(0, 16).replace(/[:T]/g, "")}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  async function unpair(d: Device) {
    if (!window.confirm(`Unpair ${d.name}? It stops posting and watching sales at once.`)) return;
    await api.del(`/extension/devices/${d.id}`);
    await load();
  }

  return (
    <section className="rail" id="extension">
      <h2 className="eyebrow rail-label">Browser extension</h2>
      <p className="hint">
        The Thrifty Crosslister extension posts your listings to Depop, Poshmark and Vinted from a browser
        that is signed in to them, checks those sites for sales every 15 minutes, and takes an item down
        everywhere else once it sells. Pair each browser that should do this; two can run side by side.
      </p>

      {devices.map((d) => (
        <div key={d.id} className="tag" style={{ cursor: "default" }}>
          <span className="tag-body">
            <span className="tag-title">{d.name}</span>
            <span className="tag-meta">
              <span style={{ color: d.online ? "var(--tag-ok, #2f7a46)" : undefined }}>
                {d.online ? (d.status.paused ? "Online, paused" : "Online") : `Last seen ${ago(d.lastSeenAt)}`}
              </span>
              {d.status.signedIn && (
                <span>
                  Signed in: {Object.entries(d.status.signedIn).filter(([, v]) => v).map(([k]) => k).join(", ") || "none yet"}
                </span>
              )}
              {d.version && <span>v{d.version}</span>}
            </span>
          </span>
          <span className="row-actions">
            <button className="quiet danger" onClick={() => void unpair(d)}>Unpair</button>
          </span>
        </div>
      ))}

      <div className="row">
        <Field label="Name for this browser">
          <input value={name} placeholder="Mac mini" onChange={(e) => setName(e.target.value)} />
        </Field>
      </div>
      <div className="actions">
        {present && !present.paired && (
          <button onClick={() => void pair(true)} disabled={busy}>Pair this browser</button>
        )}
        <button className={present && !present.paired ? "quiet" : undefined} onClick={() => void pair(false)} disabled={busy}>
          Show a pairing code
        </button>
      </div>
      {present?.paired && <p className="hint">The extension in this browser is already paired{present.name ? ` as ${present.name}` : ""}.</p>}
      {!present && <p className="hint">No extension detected in this browser. Install it, or pair another browser with a code.</p>}
      {code && (
        <div className="notice" style={{ wordBreak: "break-all" }}>
          Paste this into the extension's popup. It is shown only once:
          <code style={{ display: "block", marginTop: 6 }}>{code}</code>
        </div>
      )}
      {message && <p className="hint">{message}</p>}

      {recordings.length > 0 && (
        <>
          <h3 className="eyebrow" style={{ marginTop: 16 }}>Recorded listing sessions</h3>
          {recordings.map((r) => (
            <div key={r.id} className="tag" style={{ cursor: "default" }}>
              <span className="tag-body">
                <span className="tag-title">{r.sites.join(", ") || "No marketplace requests"}</span>
                <span className="tag-meta">
                  <span>{new Date(r.createdAt).toLocaleString()}</span>
                  <span>{r.eventCount} requests</span>
                  {r.deviceName && <span>{r.deviceName}</span>}
                </span>
              </span>
              <span className="row-actions">
                <button className="quiet" onClick={() => void download(r)}>Download</button>
              </span>
            </div>
          ))}
        </>
      )}
    </section>
  );
}
